import React, { useState, useRef, RefObject, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import ReCAPTCHA from "react-google-recaptcha";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { submitIssueReport, IssueReportData } from "../../services/IssueReportService";
import { DeviceInfo } from "../../utils/DeviceInfo";
import phoneDarlingsLogo from "../../assets/images/phoneDarlingsLogo.svg";
import RctPageLoader from "../../component/RctPageLoader";
import { siteKey } from "../../constant/default";
import { useSelector } from "react-redux";
import { auth_details } from "../../reducer/auth";
import { InputLabel, TextField } from "@mui/material";

const ReportIssue: React.FC = () => {
  const navigate = useNavigate();
  const captchaRef = useRef<ReCAPTCHA>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [screenshots, setScreenshots] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [formStartTime, setFormStartTime] = useState<number>(Date.now());
  const [mouseMovements, setMouseMovements] = useState<number>(0);
  const auth = useSelector(auth_details);
  
  // Track form interactions for bot detection
  useEffect(() => {
    const trackMouseMove = () => {
      setMouseMovements(prev => prev + 1);
    };
    
    window.addEventListener('mousemove', trackMouseMove);
    
    return () => {
      window.removeEventListener('mousemove', trackMouseMove);
    };
  }, []);
  
  // Define form validation schema
  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    email: Yup.string().email("Invalid email address").required("Email is required"),
    issueType: Yup.string().required("Please select issue type"),
    description: Yup.string().required("Description is required").min(10, "Please provide more details (minimum 10 characters)"),
  });

  // Define form values interface
  interface FormValues {
    name: string;
    email: string;
    issueType: string;
    description: string;
    notRobot: string; // Honeypot field (should remain empty)
  }

  // Handle form submission
  const handleSubmit = async (values: FormValues, { resetForm }: { resetForm: () => void }) => {
    // Check for bot behavior
    const formCompletionTime = Date.now() - formStartTime;
    const isLikelyBot = 
      formCompletionTime < 3000 || // Form completed too quickly
      mouseMovements < 5 ||      // Too few mouse movements
      values.notRobot !== "";    // Honeypot field was filled
    
    if (isLikelyBot) {
      toast.error("We detected unusual behavior. Please try again.", {
        theme: "colored",
        autoClose: 5000,
      });
      setFormStartTime(Date.now()); // Reset timing
      setMouseMovements(0);
      return;
    }
    setIsLoading(true);
    
    try {
      // Get reCAPTCHA token with double verification
      const captcha = captchaRef?.current;
      let tokenData = null;
      
      try {
        if (captcha) {
          // First attempt
          tokenData = await captcha.executeAsync();
          
          // Extra verification for suspicious submissions
          if (formCompletionTime < 10000 || mouseMovements < 10) {
            await new Promise(resolve => setTimeout(resolve, 1000)); // Add delay to deter bots
            // Second verification
            captcha.reset();
            tokenData = await captcha.executeAsync();
          }
        }
      } catch (err) {
        console.error('reCAPTCHA error:', err);
        tokenData = null;
      }
      
      if (!tokenData) {
        toast.error("Please verify that you are not a robot", {
          theme: "colored",
          autoClose: 5000,
        });
        setIsLoading(false);
        return;
      }
      
      // No need to process the screenshots - we'll pass the File objects directly
      
      // Create report data object
      const reportData = {
        name: values.name,
        email: values.email,
        issueType: values.issueType,
        description: values.description,
        screenshotFiles: screenshots.length > 0 ? screenshots : undefined, // Pass the array of File objects
        userId: auth?.user_profile?.id || undefined,
        userData: auth?.user_profile || undefined, // Pass the entire user profile for additional info
      };
      
      // Submit report
      await submitIssueReport(reportData);
      
      // Reset form and state
      resetForm();
      setScreenshots([]);
      setPreviewUrls([]);
      
      // Redirect after successful submission
      setTimeout(() => {
        navigate("/");
      }, 2000);
      
    } catch (error) {
      console.error("Error submitting issue report:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle screenshot upload
  const handleScreenshotChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
  
    const newScreenshots: File[] = [...screenshots];
    const newPreviewUrls: string[] = [...previewUrls];
  
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
  
      if (newScreenshots.length >= 3) {
        toast.warn("You can upload up to 3 screenshots only.", {
          theme: "colored",
          autoClose: 5000,
        });
        break;
      }
  
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} is not an image file`, {
          theme: "colored",
          autoClose: 5000,
        });
        continue;
      }
  
      if (file.size > 2 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Max 2MB allowed.`, {
          theme: "colored",
          autoClose: 7000,
        });
        continue;
      }
  
      newScreenshots.push(file);
  
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        newPreviewUrls.push(reader.result as string);
        setPreviewUrls([...newPreviewUrls]);
      };
    }
  
    setScreenshots([...newScreenshots]);
  };
  

  // Remove specific screenshot
  const handleRemoveScreenshot = (index: number) => {
    const newScreenshots = [...screenshots];
    const newPreviewUrls = [...previewUrls];
    
    newScreenshots.splice(index, 1);
    newPreviewUrls.splice(index, 1);
    
    setScreenshots(newScreenshots);
    setPreviewUrls(newPreviewUrls);
    
    if (newScreenshots.length === 0 && fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  // Remove all screenshots
  const handleRemoveAllScreenshots = () => {
    setScreenshots([]);
    setPreviewUrls([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <>
      {isLoading && <RctPageLoader />}
      <div className="bg-[#F8F3FD] p-2 lg:p-6 min-h-screen">
        <div className="w-full bg-[#FFFFFF] h-[80px] rounded-b-full rounded-t-full">
          <div className="flex justify-center pt-[30px] font-bold flex-shrink-0 text-gray-800">
            <img
              src={phoneDarlingsLogo}
              alt="logo"
              className="mx-auto w-[183px] h-[26px]"
            />
          </div>
        </div>

        <div className="mt-4 lg:mt-2 w-full flex">
          <div className="lg:p-2 sm:p-0 w-full">
            <Formik<FormValues>
              initialValues={{
                name: auth?.user_profile?.firstName ? `${auth.user_profile.firstName} ${auth.user_profile.lastName || ''}` : '',
                email: auth?.user_profile?.email || '',
                issueType: '',
                description: '',
                notRobot: '', // Honeypot field - should remain empty
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ errors, touched, values, handleChange }) => (
                <Form className="lg:p-2">
                  <div className="py-6 place-content-center bg-[#FFFFFF] rounded-[25px] p-2 h-full w-full mx-auto">
                    <div className="w-full flex items-center">
                      <div
                        className="ml-6 mr-2 cursor-pointer"
                        onClick={() => navigate(-1)}
                      >
                        <svg 
                          width="24" 
                          height="24" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path 
                            d="M15 18L9 12L15 6" 
                            stroke="#673AB7" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>

                      <div className="mx-auto">
                        <p className="w-full font-jaldi text-center text-[32px] font-semibold">
                          Report an Issue
                        </p>
                        <p className="w-full text-center text-base font-normal">
                          We're sorry you're experiencing problems. Please tell us what's happening.
                        </p>
                      </div>
                    </div>

                    <div className="mt-6 mx-auto font-jaldi w-11/12 lg:w-[70%]">
                      <div className="grid grid-cols-1 gap-[20px]">
                        <div>
                          <label className="form-label inline-block text-lg font-normal text-black ml-0.5">
                            Your Name
                          </label>
                          <Field
                            as={TextField}
                            type="text"
                            name="name"
                            fullWidth
                            size="small"
                            value={values.name}
                            onChange={handleChange}
                            className={`px-3 py-1.5 text-base form-control block w-full font-normal text-gray-700 bg-[#ffffff80] bg-clip-padding border border-solid rounded transition ease-in-out m-0 focus:text-gray-700 focus:bg-white focus:outline-none ${
                              errors.name && touched.name && "border-[#E85626]"
                            }`}
                          />
                          <ErrorMessage
                            name="name"
                            component="div"
                            className="text-[#E85626] text-sm"
                          />
                        </div>

                        <div>
                          <label className="form-label inline-block text-lg font-normal text-black ml-0.5">
                            Email Address
                          </label>
                          <Field
                            as={TextField}
                            type="email"
                            name="email"
                            fullWidth
                            size="small"
                            value={values.email}
                            onChange={handleChange}
                            className={`px-3 py-1.5 text-base form-control block w-full font-normal text-gray-700 bg-[#ffffff80] bg-clip-padding border border-solid rounded transition ease-in-out m-0 focus:text-gray-700 focus:bg-white focus:outline-none ${
                              errors.email && touched.email && "border-[#E85626]"
                            }`}
                          />
                          <ErrorMessage
                            name="email"
                            component="div"
                            className="text-[#E85626] text-sm"
                          />
                        </div>

                        <div>
                          <label className="form-label inline-block text-lg font-normal text-black ml-0.5">
                            Issue Type
                          </label>
                          <Field
                            as="select"
                            name="issueType"
                            value={values.issueType}
                            onChange={handleChange}
                            className={`px-3 py-2.5 text-base form-control block w-full font-normal text-gray-700 bg-[#ffffff80] bg-clip-padding border border-solid border-[#C9C9C9] rounded transition ease-in-out m-0 focus:text-gray-700 focus:bg-white focus:outline-none ${
                              errors.issueType && touched.issueType && "border-[#E85626]"
                            }`}
                          >
                            <option value="">Select Issue Type</option>
                            <option value="Login Issues">Login Issues</option>
                            <option value="Profile Display Problems">Profile Display Problems</option>
                            <option value="Call Disconnections">Call Disconnections</option>
                            <option value="Payment Issues">Payment Issues</option>
                            <option value="App Crashes">App Crashes</option>
                            <option value="Slow Performance">Slow Performance</option>
                            <option value="Other">Other</option>
                          </Field>
                          <ErrorMessage
                            name="issueType"
                            component="div"
                            className="text-[#E85626] text-sm"
                          />
                        </div>

                        <div>
                          <label className="form-label inline-block text-lg font-normal text-black ml-0.5">
                            Describe the Issue
                          </label>
                          <Field
                            as="textarea"
                            name="description"
                            rows={5}
                            value={values.description}
                            onChange={handleChange}
                            placeholder="Please provide details about what happened, what you were doing, and any error messages you saw."
                            className={`px-3 py-2 text-base form-control block w-full font-normal text-gray-700 bg-[#ffffff80] bg-clip-padding border border-solid border-[#C9C9C9] rounded transition ease-in-out m-0 focus:text-gray-700 focus:bg-white focus:outline-none ${
                              errors.description && touched.description && "border-[#E85626]"
                            }`}
                          />
                          <ErrorMessage
                            name="description"
                            component="div"
                            className="text-[#E85626] text-sm"
                          />
                        </div>

                        <div>
                          <label className="form-label inline-block text-lg font-normal text-black ml-0.5">
                            Screenshots (helps us diagnose issues)
                          </label>
                          <div className="mt-2">
                            <input
                              type="file"
                              accept="image/*"
                              ref={fileInputRef}
                              onChange={handleScreenshotChange}
                              className="hidden"
                              multiple
                            />
                            <button
                              type="button"
                              onClick={() => fileInputRef.current?.click()}
                              className="bg-[#EFE5F8] text-[#673AB7] py-2 px-4 rounded-lg mr-2"
                            >
                              Upload Screenshots
                            </button>
                            {screenshots.length > 0 && (
                              <button
                                type="button"
                                onClick={handleRemoveAllScreenshots}
                                className="bg-[#FFE5E5] text-[#E85626] py-2 px-4 rounded-lg"
                              >
                                Remove All
                              </button>
                            )}
                          </div>
                          {previewUrls.length > 0 && (
                            <div className="mt-3 border rounded p-2 bg-gray-50">
                              <p className="text-sm text-gray-600 mb-2">Screenshot Previews:</p>
                              <div className="flex flex-wrap gap-3">
                                {previewUrls.map((url, index) => (
                                  <div key={index} className="relative">
                                    <div className="relative group">
                                      <img
                                        src={url}
                                        alt={`Screenshot preview ${index + 1}`}
                                        className="max-w-[150px] max-h-[150px] object-contain border"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveScreenshot(index)}
                                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-80 hover:opacity-100"
                                        title="Remove this screenshot"
                                      >
                                        ×
                                      </button>
                                    </div>
                                    <p className="text-xs text-center mt-1 text-gray-500">{screenshots[index]?.name}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          <p className="text-sm text-gray-500 mt-1">
                            <strong>Max file size: 2MB per file</strong>. Supported formats: JPG, PNG, GIF. You can upload multiple screenshots.
                          </p>
                        </div>
                      </div>

                      <div className="mt-6">
                        <p className="text-sm text-gray-500">
                          By submitting this form, we'll collect your browser and device information to help diagnose the issue.
                        </p>
                        
                        {/* Honeypot field - hidden from real users but bots might fill it */}
                        <div style={{ display: 'none' }}>
                          <Field
                            type="text"
                            name="notRobot"
                            id="notRobot"
                            tabIndex="-1"
                            autoComplete="off"
                          />
                        </div>
                      </div>

                      <div>
                        {/* @ts-ignore - ReCAPTCHA typing issues */}
                        <ReCAPTCHA
                          sitekey={siteKey}
                          ref={captchaRef}
                          size="invisible"
                        />
                      </div>

                      <div className="text-center w-full mt-8 font-jaldi">
                        <button
                          className="py-2 w-full md:w-1/2 bg-[#673AB7] rounded-lg text-xl text-white"
                          type="submit"
                        >
                          Submit Report
                        </button>
                      </div>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReportIssue;